import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchFamilyFeedback } from "../../../store/slices/familyFeedbackSlice";
import { FAMILY_SECTIONS, FAMILY_LIKERT_LEGEND } from "../../../pages/FamilyFeedback/familyFeedbackConfig";
import PrintWrapper, { printFooterStyles } from "../../common/PrintWrapper";
import logo from "../../../assets/images/logo-samplee.png";

const val = (v) => (v === null || v === undefined || v === "" ? "—" : v);
const fmt = (v) => (v ? new Date(v).toLocaleDateString("en-GB") : "—");

export default function FamilyFeedbackPrint() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { currentFamilyFeedback, isLoading, error } = useSelector((s) => s.familyFeedback);

  useEffect(() => {
    if (id) dispatch(fetchFamilyFeedback(id));
  }, [dispatch, id]);

  useEffect(() => {
    if (currentFamilyFeedback) {
      const t = setTimeout(() => window.print(), 800);
      window.onafterprint = () => navigate(-1);
      return () => { clearTimeout(t); window.onafterprint = null; };
    }
  }, [currentFamilyFeedback, navigate]);

  if (isLoading) return <div className="p-4 text-center">Loading Family Feedback print...</div>;
  if (error) return <div className="p-4 text-danger text-center">{error}</div>;
  if (!currentFamilyFeedback) return null;

  const d = currentFamilyFeedback;
  const mark = (key, n) => (Number(d[key]) === n ? "☑" : "☐");

  return (
    <>
      <style jsx global>{`
        body { margin: 0 !important; padding: 0 !important; }
        ${printFooterStyles}
        @media print {
          @page { size: A4; margin: 12mm 12mm; }
          body { background: #fff !important; color: #000 !important; }
          .no-print { display: none !important; }
          .section, table, .q-block { page-break-inside: avoid; }
          * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          table, th, td { border: 1px solid #000 !important; }
          .section-head { background: #e9e9e9 !important; }
          .legend { background: #fafafa !important; border: 1px solid #000 !important; }
          .q-block { border: 1px solid #000 !important; }
        }
        * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        .ff-print, .ff-print * { font-family: "Times New Roman", Times, serif; color: #000; }
        .ff-print { width: 100%; max-width: 820px; margin: 0 auto; padding: 16px; font-size: 10pt; line-height: 1.3; }
        .header-logo { text-align: center; margin-bottom: 4px; }
        .header-logo img { height: 58px; }
        .doc-title { text-align: center; font-size: 15pt; font-weight: bold; text-transform: uppercase; margin: 0 0 8px; }
        table { width: 100%; border-collapse: collapse; margin-bottom: 8px; }
        td, th { border: 1px solid #000; padding: 3px 6px; font-size: 9pt; vertical-align: top; }
        .lbl { font-weight: bold; background: #f7f7f7; width: 24%; }
        .legend { border: 1px solid #000; background: #fafafa; padding: 6px 8px; margin-bottom: 10px; font-size: 8.8pt; }
        .section-head { background: #e9e9e9; font-weight: bold; font-size: 10pt; padding: 3px 6px; border: 1px solid #000; margin-top: 8px; }
        .q-block { border: 1px solid #000; padding: 6px 8px; margin-bottom: 6px; font-size: 9pt; }
        .q-title { font-weight: bold; margin-bottom: 2px; }
        .q-options { margin: 2px 0; font-size: 9pt; }
        .q-options span { margin-right: 12px; }
        .q-comment { font-style: italic; color: #333; margin-top: 2px; }
        .notes-box { border: 1px solid #000; min-height: 40px; padding: 5px 7px; white-space: pre-wrap; font-size: 9.5pt; }
        .footer { text-align: center; font-size: 8.5pt; margin-top: 10px; border-top: 1px solid #000; padding-top: 4px; }
      `}</style>

      <PrintWrapper>
        <div className="ff-print">
          <div className="header-logo"><img src={logo} alt="The Recovery House" /></div>
          <h1 className="doc-title">Family Feedback Form &amp; Discharge Interview</h1>

          {/* Section A: Basic Information */}
          <div className="section-head">Section A: Basic Information</div>
          <table>
            <tbody>
            <tr><td className="lbl">Client Name</td><td>{val(d.client_name)}</td><td className="lbl">Date of Discharge</td><td>{fmt(d.date_of_discharge)}</td></tr>
            <tr><td className="lbl">Interviewer Name</td><td colSpan={3}>{val(d.interviewer_name)}</td></tr>
            <tr><td className="lbl">Comments</td><td colSpan={3} style={{ whiteSpace: "pre-wrap" }}>{val(d.comments)}</td></tr>
            </tbody>
          </table>

        {/* Legend */}
        <div className="legend">
          <strong>Likert Scale Guide:</strong>{" "}
          {FAMILY_LIKERT_LEGEND.map((l) => `${l.n} = ${l.label}`).join("  |  ")}
        </div>

        {/* Sections with rating + comment per question */}
        {FAMILY_SECTIONS.map((section) => (
          <div className="section" key={section.title}>
            <div className="section-head">{section.title}</div>
            {section.questions.map((q) => (
              <div className="q-block" key={q.key}>
                <div className="q-label">{q.label}</div>
                <div className="q-scale">
                  <span>{mark(q.key, 1)} 1</span>
                  <span>{mark(q.key, 2)} 2</span>
                  <span>{mark(q.key, 3)} 3</span>
                  <span>{mark(q.key, 4)} 4</span>
                  <span>{mark(q.key, 5)} 5</span>
                </div>
                <div className="q-comment"><strong>Comment:</strong> {val(d[`${q.key}_comment`])}</div>
              </div>
            ))}
          </div>
        ))}

        {/* Final Comments */}
        <div className="section-head">Final Comments</div>
        <div className="notes-box">{d.final_comments || ""}</div>

        <div className="footer">CONFIDENTIAL — For Authorised Clinical Use Only</div>
      </div>
      </PrintWrapper>
    </>
  );
}
